import {
    TerraDraw,
    TerraDrawPolygonMode,
    TerraDrawCircleMode,
    TerraDrawRenderMode
} from 'terra-draw';
import { TerraDrawGoogleMapsAdapter } from 'terra-draw-google-maps-adapter';
import * as turf from './turf';
import CustomMarker from '../helpers/custom-marker';

const DISTRICT_DRAW_STROKE = {
    fillColor: '#57c9dc',
    fillOpacity: 0.3,
    outlineColor: '#57c9dc',
    outlineOpacity: 1,
    outlineWidth: 2
};

class DrawingManager {
    constructor(mapState, areaManager) {
        this.mapState = mapState;
        this.areaManager = areaManager;
        this.terraDraw = null;
        this.selectedOverlayPolygon = [];
        this.houseMarker = null;
        this.clickHouseListener = [];
        this.finishListener = null;
        this._drawingStyleEl = null;
    }

    async handleApiLoaded({ map, maps, selectArea }) {
        await this.waitForMapProjection(map, maps);

        const polygonMode = new TerraDrawPolygonMode({
            styles: {
                ...DISTRICT_DRAW_STROKE,
                closingPointWidth: 6,
                closingPointColor: '#57c9dc',
                closingPointOpacity: 1,
                closingPointOutlineWidth: 2,
                closingPointOutlineColor: '#ffffff',
                closingPointOutlineOpacity: 1,
                coordinatePointWidth: 0,
                coordinatePointColor: '#57c9dc',
                coordinatePointOpacity: 0,
                coordinatePointOutlineWidth: 0,
                coordinatePointOutlineColor: '#57c9dc',
                coordinatePointOutlineOpacity: 0
            }
        });

        const circleMode = new TerraDrawCircleMode({
            styles: { ...DISTRICT_DRAW_STROKE }
        });

        const idleMode = new TerraDrawRenderMode({
            modeName: 'idle',
            styles: {
                polygonFillColor: '#57c9dc',
                polygonFillOpacity: 0,
                polygonOutlineColor: '#57c9dc',
                polygonOutlineWidth: 0,
                pointColor: '#57c9dc',
                pointWidth: 0,
                pointOpacity: 0,
                pointOutlineColor: '#57c9dc',
                pointOutlineOpacity: 0,
                pointOutlineWidth: 0,
                lineStringWidth: 0,
                lineStringColor: '#57c9dc',
                lineStringOpacity: 0,
                zIndex: 0
            },
            pointerEvents: {
                leftClick: false,
                rightClick: false,
                contextMenu: false,
                onDragStart: false,
                onDrag: false,
                onDragEnd: false
            }
        });

        const adapter = new TerraDrawGoogleMapsAdapter({
            lib: maps,
            map,
            coordinatePrecision: 9
        });

        const draw = new TerraDraw({
            adapter,
            modes: [polygonMode, circleMode, idleMode]
        });

        this.finishListener = (id, context) => {
            if (context.action !== 'draw') {
                return;
            }

            const feature = draw.getSnapshotFeature(id);
            if (feature && feature.geometry && feature.geometry.type === 'Polygon') {
                this.handleDrawingComplete(feature, selectArea);
            }

            if (draw.hasFeature(id)) {
                draw.removeFeatures([id]);
            }

            draw.setMode('idle');
            this.mapState.styleManager.isDrawing = false;
            this.disableDrawingPointerEvents();
            if (this.mapState.tools) {
                this.mapState.tools.resetTools();
            }
        };

        draw.on('finish', this.finishListener);
        draw.start();
        draw.on('ready', () => {
            draw.setMode('idle');
        });

        this.terraDraw = draw;
        this.mapState.styleManager.setTerraDrawStyleFn(adapter.style.bind(adapter));
    }

    waitForMapProjection(map, maps) {
        return new Promise((resolve) => {
            if (map.getProjection()) {
                resolve();
                return;
            }
            maps.event.addListenerOnce(map, 'projection_changed', () => resolve());
        });
    }

    drawPolygon() {
        if (!this.terraDraw) return;
        this.mapState.styleManager.isDrawing = true;
        this.enableDrawingPointerEvents();
        this.terraDraw.setMode('polygon');
    }

    drawCircle() {
        if (!this.terraDraw) return;
        this.mapState.styleManager.isDrawing = true;
        this.enableDrawingPointerEvents();
        this.terraDraw.setMode('circle');
    }

    stopDrawing() {
        if (!this.terraDraw) return;
        this.mapState.styleManager.isDrawing = false;
        this.terraDraw.setMode('idle');
        this.disableDrawingPointerEvents();
    }

    enableDrawingPointerEvents() {
        if (this._drawingStyleEl) return;
        const map = this.mapState.getMap();
        if (!map) return;

        const mapDiv = map.getDiv();
        const targetPane = mapDiv.querySelector('div[style*="z-index: 3"]');
        if (targetPane) {
            targetPane.style.pointerEvents = 'auto';
        }

        const style = document.createElement('style');
        style.textContent = `
            .gm-style div[style*="z-index: 3"] > * {
                pointer-events: none !important;
            }
        `;
        document.head.appendChild(style);
        this._drawingStyleEl = style;
    }

    disableDrawingPointerEvents() {
        if (this._drawingStyleEl) {
            this._drawingStyleEl.remove();
            this._drawingStyleEl = null;
        }

        const map = this.mapState.getMap();
        if (!map) return;
        const mapDiv = map.getDiv();
        const targetPane = mapDiv.querySelector('div[style*="z-index: 3"]');
        if (targetPane) {
            targetPane.style.pointerEvents = '';
        }
    }

    handleDrawingComplete(feature, selectArea) {
        if (!feature || !feature.geometry || feature.geometry.type !== 'Polygon') {
            return;
        }

        const overlayCoordinates = feature.geometry.coordinates?.[0];
        if (!overlayCoordinates || overlayCoordinates.length < 4) {
            return;
        }
        const overlayPolygon = turf.polygon([overlayCoordinates]);
        const selectionType = this.mapState.getSelectionType();
        const turfPolygons = this.mapState.getTurfPolygons(selectionType) || [];
        
        // Find intersecting areas and select them
        turfPolygons.forEach(data => {
            if (data.feature.properties.type === selectionType && turf.intersect(overlayPolygon, data.polygon)) {
                selectArea(data.feature);
            }
        });
    }

    renderHouseMarker(center, draggable = false) {
        const maps = this.mapState.getMaps();
        
        // First remove any existing marker
        if (this.houseMarker) {
            this.houseMarker.setMap(null);
        }

        // Create the marker HTML
        const markerDiv = document.createElement('div');
        markerDiv.className = 'custom-marker-house';
        markerDiv.style.zIndex = '4';  // Set highest z-index
        markerDiv.innerHTML = `
            <div class="custom-marker-house__icon"></div>
            <button class="custom-marker-house__close">×</button>
        `;

        // Create the custom marker
        const marker = CustomMarker({
            maps,
            map: this.mapState.getMap(),
            latlng: center,
            draggable,
            appendNode: markerDiv
        });

        // Add click handler for the close button
        const closeButton = markerDiv.querySelector('.custom-marker-house__close');
        if (closeButton) {
            closeButton.addEventListener('click', (e) => {
                e.stopPropagation();
                marker.setMap(null);
                this.houseMarker = null;
            });
        }

        // Set the marker on the map
        marker.setMap(this.mapState.getMap());

        this.houseMarker = marker;
        return marker;
    }

    toggleDrawHouse(isToolActive) {
        if (isToolActive) {
            if (this.houseMarker) {
                this.houseMarker.setDraggable(true);
            }

            // Make all area features non-clickable while house marker tool is active
            const map = this.mapState.getMap();
            map.data.forEach((feature) => {
                const featureType = feature.getProperty('type');
                if (featureType && !featureType.startsWith('maplayer-')) {
                    map.data.overrideStyle(feature, { 
                        clickable: false,
                        zIndex: 1  // Lower z-index than house marker
                    });
                }
            });

            // Remove area click listener before adding house marker listeners
            this.mapState.styleManager.removeAreaClickEvent(this.mapState.getMaps());

            // Add click listener for the map
            const clickListener = this.mapState.getMap().addListener('click', (event) => {
                const center = event.latLng;
                this.renderHouseMarker(center, true);
            });
            this.clickHouseListener.push(clickListener);

        } else {
            // Remove house marker listeners
            this.clickHouseListener.forEach(evt => {
                this.mapState.getMaps().event.removeListener(evt);
            });
            this.clickHouseListener = [];
            
            if (this.houseMarker) {
                this.houseMarker.setDraggable(false);
            }

            // Restore clickable state for features of current selection type
            const map = this.mapState.getMap();
            const selectionType = this.mapState.getSelectionType();
            
            if (map && selectionType) {
                map.data.forEach((feature) => {
                    const featureType = feature.getProperty('type');
                    if (featureType === selectionType) {
                        map.data.overrideStyle(feature, { 
                            visible: true,
                            clickable: true,
                            zIndex: 3  // Lower than house marker's z-index of 4
                        });
                    }
                });
            }

            // Reattach area click event
            this.mapState.styleManager.attachAreaClickEvent(
                this.mapState.getMap(),
                this.mapState.getMaps(),
                (featureId) => {
                    const selectionType = this.mapState.getSelectionType();
                    
                    if (selectionType && this.mapState.turfPolygons[selectionType]) {
                        const data = this.mapState.turfPolygons[selectionType].find(data => data.feature.properties.id === featureId);
                        
                        if (data) {
                            this.areaManager.toggleArea(data.feature);
                        }
                    } else {
                    }
                }
            );
        }
    }

    toLatLng(point) {
        return new this.mapState.getMaps().LatLng(point[1], point[0]);
    }

    undo() {
        if (this.history.length > 1) {
            this.history.pop(); // Remove current state
            const previousState = this.history[this.history.length - 1];
            this.mapState.setSelectedAreas(previousState);
            return true;
        }
        return false;
    }

    getHouseMarker() {
        return this.houseMarker;
    }
}

export default DrawingManager; 